// =============================================================
// Main bootstrap — site entry point.
// =============================================================
import './styles/site.css';
import './styles/splash.css';

import { t, initI18n, applyI18n, onLocaleChange, setLocale, getLocale } from './lib/i18n';
import type { Locale } from './lib/i18n';
import { initTheme, toggleTheme, getTheme, onThemeChange } from './lib/theme';
import { initMode, toggleMode, onModeChange, getMode, setMode, isModeAuthoritative, getCachedDiscipline } from './lib/mode';
import { shouldShowSplash, mountSplash, preloadSplashImages } from './lib/splash';
import { fetchContent, fetchAgenda, TENANT_THEMES } from './lib/api';
import type { ContentTree, PageRecord, NavItem } from './lib/content';
import { DEFAULT_PAGES, visibleNavPages, ensureNavItems, HOME_PAGE_IDS, MAIN_PAGE_ID, MAIN_YOGA_PAGE_ID } from './lib/content';
import { buildSeo, applySeoToHead } from './lib/seo';
import {
  heroTpl, aboutTpl, disciplinesTpl, locationsTpl, contactTpl, agendaTpl,
  pageTpl, renderMarkdown,
} from './lib/templates';
import { sanitizeHtml } from './lib/sanitize';
import { expandWidgets } from './lib/widgets';
import { fetchMe } from './lib/userApi';
import { fetchTakenSlots } from './lib/userApi';
import { renderUserRoute, renderHeaderUser } from './lib/userRouter';
import { mountPublicAgenda } from './lib/publicAgenda';

// ---------- Tiny DOM helpers ----------
const $  = <T extends Element = Element>(sel: string, scope: ParentNode = document) =>
  scope.querySelector(sel) as T | null;
const $$ = <T extends Element = Element>(sel: string, scope: ParentNode = document) =>
  Array.from(scope.querySelectorAll(sel)) as T[];

// =============================================================
// Global state
// =============================================================
let tree: ContentTree | null = null;

// =============================================================
// Year in footer
// =============================================================
$('#year')?.replaceWith(Object.assign(document.createElement('span'), {
  id: 'year', textContent: String(new Date().getFullYear()),
}));

// =============================================================
// I18N + re-render subscription
// =============================================================
const initialLocale: Locale = initI18n();
// Init theme mirrors the data-theme attribute already on <html>
// (the inline boot script in index.html applied it before paint).
initTheme();
// Init mode mirrors the data-mode attribute already on <html>
// (parallel inline boot script in index.html). Strength wins by default.
initMode();

// =============================================================
// Nav rendering
// =============================================================
/** Convert a NavItem into a route-friendly slug, or `null` for external. */
function navItemHref(item: NavItem): { href: string; slug: string; external: boolean } | null {
  if (item.kind === 'link') {
    const isInternal = item.url.startsWith('/') || item.url.startsWith('#');
    return { href: item.url, slug: '', external: !isInternal };
  }
  // page-kind: look up the page slug
  if (stateTreeRef?.pages) {
    const p = stateTreeRef.pages.find(x => x.id === item.pageId);
    if (p?.slug) return { href: `/${p.slug}`, slug: p.slug, external: false };
    // Main page: empty slug → home
    if (p && p.slug === '') return { href: '/', slug: '', external: false };
  }
  return null;
}

/** Module-level reference to the latest content tree so navItemHref can peek. */
let stateTreeRef: ContentTree | null = null;

function renderNav(): void {
  const menu = $('#navMenu');
  if (!menu) return;

  // Clear existing children safely
  menu.replaceChildren();

  // Prefer the flat navItems list; fall back to pages when it's missing.
  const items = tree && tree.navItems?.length
    ? ensureNavItems(tree).filter(i => i.showInNav).sort((a, b) => a.navOrder - b.navOrder)
    : null;

  const locale = getLocale();

  if (items) {
    for (const item of items) {
      const info = navItemHref(item);
      if (!info) continue;
      const a = document.createElement('a');
      a.href = info.href;
      a.dataset.nav = '';
      if (info.external) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      } else if (info.slug) {
        a.dataset.slug = info.slug;
      }
      const text = item.kind === 'page'
        ? (() => {
            const p = tree?.pages.find(x => x.id === item.pageId);
            return p ? (p.navLabel[locale] || p.navLabel.hu) : '';
          })()
        : (item.label[locale] || item.label.hu);
      a.textContent = text || info.href;
      menu.appendChild(a);
    }
  } else {
    // Backward-compat: build nav from pages only.
    const pages = tree ? visibleNavPages(tree) : DEFAULT_PAGES.filter(p => p.showInNav);
    for (const p of pages) {
      const a = document.createElement('a');
      a.href = p.slug ? `/${p.slug}` : '/';
      a.dataset.nav = '';
      a.dataset.slug = p.slug;
      a.textContent = p.navLabel[locale] || p.navLabel.hu;
      menu.appendChild(a);
    }
  }

  // Language toggle button
  const langBtn = document.createElement('button');
  langBtn.type = 'button';
  langBtn.className = 'nav__lang';
  langBtn.id = 'navLang';
  langBtn.setAttribute('aria-label', 'Switch language');
  const langLabel = document.createElement('span');
  langLabel.dataset.i18n = 'lang.label';
  langLabel.textContent = t('lang.label');
  langBtn.appendChild(langLabel);
  menu.appendChild(langBtn);

  // Theme toggle button (sun/moon icon, swap aria-label on flip).
  const themeBtn = document.createElement('button');
  themeBtn.type = 'button';
  themeBtn.className = 'nav__theme';
  themeBtn.id = 'navTheme';
  // Build the icon as DOM nodes (never innerHTML) — same hard-coded
  // shapes as the admin toggle, but constructed safely so the markup
  // can't be reinterpreted even if a future refactor accidentally
  // threads user input into the icon strings.
  const renderIcon = () => {
    themeBtn.replaceChildren();
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    if (getTheme() === 'dark') {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12'); circle.setAttribute('cy', '12'); circle.setAttribute('r', '4');
      const rays = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      rays.setAttribute('d', 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41');
      svg.append(circle, rays);
    } else {
      const moon = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      moon.setAttribute('d', 'M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z');
      svg.append(moon);
    }
    themeBtn.append(svg);
    const label = getTheme() === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark');
    themeBtn.setAttribute('aria-label', label);
    themeBtn.setAttribute('title', label);
  };
  renderIcon();
  themeBtn.addEventListener('click', () => {
    toggleTheme();
  });
  // Re-render the icon if the theme flips from another widget.
  onThemeChange(renderIcon);
  menu.appendChild(themeBtn);

  // CTA stays last
  const cta = document.createElement('a');
  cta.className = 'nav__cta';
  cta.href = '/contact';
  cta.dataset.nav = '';
  cta.dataset.slug = 'contact';
  cta.dataset.i18n = 'nav.book';
  cta.textContent = t('nav.book');
  menu.appendChild(cta);

  langBtn.addEventListener('click', () => {
    const next: Locale = getLocale() === 'hu' ? 'en' : 'hu';
    setLocale(next, { syncPath: true });
  });
}

// =============================================================
// Page rendering
// =============================================================
function renderHome(app: HTMLElement): void {
  // If the Main page is editable and has content, render it from the DB
  // instead of the hard-coded template composition. The Main page owns the
  // whole home view; admins can drop in any HTML.
  //
  // NOTE: we render the body RAW (no .page__body wrapper) because the home
  // view contains interactive sections (hero, contact form, agenda) whose
  // own .reveal animations need to fire — wrapping the whole thing in
  // another reveal container would never meet the IntersectionObserver
  // threshold.
  //
  // Widget markers (<span class="widget-marker" data-widget="contact">)
  // are expanded after sanitization so admins can drop them anywhere in
  // the body.
  const main = getHomePage();
  const locale = getLocale();
  const body = main?.body[locale] || main?.body.hu;
  if (main && body && body.trim().length > 0) {
    app.innerHTML = sanitizeHtml(body);
    expandWidgets(app);
  } else {
    app.innerHTML =
      heroTpl() +
      aboutTpl() +
      disciplinesTpl() +
      locationsTpl() +
      agendaTpl() +
      contactTpl();
  }
}

// Render a page body. Admin-authored content is HTML (sanitized) by default;
// plain text without tags is treated as legacy Markdown for backward compat.
function renderBody(body: string): string {
  if (!body) return '';
  const looksLikeHtml = /<[a-z][^>]*>/i.test(body);
  return looksLikeHtml ? body : renderMarkdown(body);
}

function renderDynamicPage(app: HTMLElement, page: PageRecord): void {
  const locale = getLocale();
  // Main page: body controls everything, no auto-h1 (it's the home view).
  const isMain = page.id === 'main';
  const title = isMain ? '' : (page.navLabel[locale] || page.navLabel.hu);
  const rawHtml = renderBody(page.body[locale] || page.body.hu || '');
  const safeHtml = sanitizeHtml(rawHtml);
  // pageTpl() returns trusted structure with escaped values; we still sanitize
  // the inner bodyHtml which is admin-controlled. Widget markers
  // (<span class="widget-marker" data-widget="…">) expand inside the body
  // so any page can drop in the contact form or agenda calendar.
  app.innerHTML = pageTpl(page.id, title, safeHtml);
  expandWidgets(app);
}

// Built-in section slugs map to home-page sections (scroll, not dynamic render).
// `services` is the public slug, `disciplines` is the section id.
const BUILT_IN_SECTIONS = ['about', 'services', 'locations', 'contact', 'agenda'] as const;

/** Parse the current URL pathname into a slug, stripping any leading locale prefix. */
function currentSlug(): string {
  const path = location.pathname || '/';
  const stripped = path.replace(/^\/(?:hu|en)(?=\/|$)/, '');
  const seg = stripped.replace(/^\//, '').split('/').filter(Boolean);
  return seg[0] || '';
}

/** Resolve the home page record for the current mode. Both 'main' and
 *  'main-yoga' share slug '' (they both render at `/`), so a slug-based
 *  lookup would be ambiguous — we look up by `id` instead and fall back
 *  to whichever home page exists if the mode-specific one hasn't been
 *  authored yet (so `/` never goes blank while the admin fills things in). */
function getHomePage(): PageRecord | null {
  if (!tree) return null;
  const id = getMode() === 'yoga' ? MAIN_YOGA_PAGE_ID : MAIN_PAGE_ID;
  return (
    tree.pages.find(p => p.id === id)
    ?? tree.pages.find(p => HOME_PAGE_IDS.has(p.id))
    ?? null
  );
}

/** Navigate to a path using the History API without triggering a server request. */
export function navigate(href: string): void {
  if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
  // Normalise: drop any in-page hash and route via pushState
  const [pathPart] = href.split('#');
  const target = pathPart || '/';
  if (target === location.pathname) return;
  history.pushState({}, '', target);
  renderRoute();
  // Best-effort refresh of the cached user so admin-side changes (e.g.
  // discipline flip) propagate without forcing a logout/login cycle.
  // fetchMe() writes the cache and 401 clears it; either way the
  // onModeChange subscriber will re-assert if the discipline differs.
  void refreshCachedUserAndMode();
}

/** Re-fetch the session user (no UI block), update the localStorage
 *  cache, and snap the site mode to the member's discipline if it
 *  changed server-side. Safe to call on every navigation — a 401 just
 *  clears the cache (logged out). */
async function refreshCachedUserAndMode(): Promise<void> {
  const u = await fetchMe().catch(() => null);
  if (!u) return;
  if (isModeAuthoritative()) {
    const d = getCachedDiscipline();
    if (d && d !== getMode()) setMode(d);
  }
}

function renderRoute(): void {
  const app = $('#app') as HTMLElement | null;
  if (!app) return;

  const path = location.pathname;
  const slug = currentSlug();

  // User-facing routes take precedence over the public site.
  const handled = renderUserRouteSync(path);
  if (handled) {
    renderHeaderUser();
    refreshActiveNav();
    applySeoForCurrentRoute();
    return;
  }

  if (!slug || (BUILT_IN_SECTIONS as readonly string[]).includes(slug)) {
    // Home (or jump to a built-in section via /slug).
    renderHome(app);
    if (slug) {
      const id = slug === 'services' ? 'disciplines' : slug;
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  } else {
    const page = tree?.pages.find(p => p.slug === slug);
    if (page) {
      renderDynamicPage(app, page);
    } else {
      app.innerHTML = `<section class="page"><div class="page__inner"><h1 class="section-title">404</h1></div></section>`;
    }
  }

  applyI18n(app);
  wireInteractivity();
  refreshActiveNav();
  applySeoForCurrentRoute();
}

/**
 * Synchronous version of renderUserRoute that returns true if the
 * path matched a user route (the actual render is async but we
 * fire-and-forget it).
 */
function renderUserRouteSync(path: string): boolean {
  const stripped = path.replace(/^\/(?:hu|en)(?=\/|$)/, '');
  if (stripped === '/login' || stripped === '/login/change-password' ||
      stripped === '/me' || stripped === '/agenda') {
    void renderUserRoute(path);
    return true;
  }
  return false;
}

// =============================================================
// Active nav highlight + SEO per route
// =============================================================
function refreshActiveNav(): void {
  const slug = currentSlug();
  $$<HTMLAnchorElement>('[data-nav]', $('#navMenu') ?? document).forEach(a => {
    const href = a.getAttribute('href') ?? '';
    // Match internal page links by their trailing slug: /about → 'about', / → ''
    const hrefSlug = href.startsWith('/') ? (href.replace(/^\//, '').split('/')[0] || '') : '';
    a.classList.toggle('is-active', hrefSlug === slug);
  });
}

function applySeoForCurrentRoute(): void {
  const slug = currentSlug();

  // Home: resolve the Main page so its SEO override applies.
  const page = !slug
    ? getHomePage()
    : tree?.pages.find(p => p.slug === slug) ?? null;
  const seo = buildSeo({
    page: page ?? null,
    locale: getLocale(),
    canonicalPath: slug ? `/${slug}` : '/',
    fallbackTitle: t('site.name'),
    fallbackDescription: t('about.lede'),
  });
  applySeoToHead(seo);
}

// =============================================================
// Interactivity wiring — re-applied after every render
// =============================================================

/**
 * Observe every `.reveal` element currently in the DOM and add the
 * `.is-visible` class as it enters the viewport. Idempotent: elements
 * already visible are left alone, and re-invoking creates a new
 * observer only for elements that haven't been revealed yet.
 *
 * Exported so user-route renderers (login, change-password, profile,
 * agenda) can call it after injecting their own HTML — those pages
 * don't go through `wireInteractivity()` because the public-site
 * router short-circuits on user routes.
 */
export function wireReveals(): void {
  const reveals = $$<HTMLElement>('.reveal:not(.is-visible)');
  if (reveals.length === 0) return;
  if (!('IntersectionObserver' in window)) {
    reveals.forEach(el => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        io.unobserve(e.target);
      }
    }
  }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });
  reveals.forEach(el => io.observe(el));
}

function wireInteractivity(): void {
  // Hero service rows: click-to-expand
  $$<HTMLLIElement>('.hero__service').forEach((row) => {
    row.tabIndex = 0;
    row.addEventListener('click', () => row.classList.toggle('is-open'));
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        row.classList.toggle('is-open');
      }
    });
  });

  // Disciplines tabs
  const tabs = $$<HTMLButtonElement>('[role="tab"][data-tab]');
  const panels = $$<HTMLElement>('[role="tabpanel"][data-panel]');
  const activate = (name: string) => {
    tabs.forEach(tab => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    panels.forEach(panel => {
      const active = panel.dataset.panel === name;
      panel.classList.toggle('is-active', active);
      if (active) panel.removeAttribute('hidden'); else panel.setAttribute('hidden', '');
    });
  };
  tabs.forEach(tab => tab.addEventListener('click', () => {
    const n = tab.dataset.tab; if (n) activate(n);
  }));
  tabs.forEach(tab => tab.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const order = tabs.map(t => t.dataset.tab ?? '');
    const i = order.indexOf(tab.dataset.tab ?? '');
    const next = e.key === 'ArrowRight'
      ? order[(i + 1) % order.length]
      : order[(i - 1 + order.length) % order.length];
    activate(next);
    tabs.find(x => x.dataset.tab === next)?.focus();
  }));

  // Scroll reveal — also exported as `wireReveals` so user-route
  // renderers (/login, /me, /agenda, /login/change-password) can
  // observe their own .reveal sections. Without this those pages
  // stay invisible (opacity:0) because wireInteractivity() short-
  // circuits before reaching this block on user routes.
  wireReveals();

  // Counters
  const counters = $$<HTMLElement>('[data-counter]');
  const animateCount = (el: HTMLElement) => {
    const target = Number(el.dataset.counter ?? '0');
    const dur = 1400;
    const start = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      el.textContent = String(Math.round(ease(t) * target));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if ('IntersectionObserver' in window && counters.length) {
    const co = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          animateCount(e.target as HTMLElement);
          co.unobserve(e.target);
        }
      }
    }, { threshold: 0.4 });
    counters.forEach(c => co.observe(c));
  }

  // Contact form
  const form = $('#contactForm') as HTMLFormElement | null;
  const status = $('#formStatus');
  if (form && status) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      status.classList.remove('is-success', 'is-error');
      status.textContent = t('contact.sending');
      if (!form.checkValidity()) {
        status.classList.add('is-error');
        status.textContent = t('contact.error');
        form.reportValidity();
        return;
      }
      try {
        await new Promise(r => setTimeout(r, 700));
        status.classList.add('is-success');
        status.textContent = t('contact.success');
        form.reset();
      } catch {
        status.classList.add('is-error');
        status.textContent = t('contact.errorGeneric');
      }
    });
  }

  // Agenda placeholder render
  renderAgenda();
}

async function renderAgenda(): Promise<void> {
  const mountA22 = document.getElementById('agendaMountA22');
  const mountM15 = document.getElementById('agendaMountM15');
  if (!mountA22 || !mountM15) return;

  // Render a shimmer skeleton first so the cards reserve their
  // vertical space. Passing `null` (instead of `[]`) is the loading
  // sentinel — classifying an empty array would flash "everything
  // is free" for the duration of the fetch and then correct itself,
  // which reads as a bug.
  mountPublicAgenda('agendaMountA22', 'A22', null);
  mountPublicAgenda('agendaMountM15', 'M15', null);

  try {
    // Fetch the admin-defined events AND the public "taken" list
    // (approved trainee bookings that effectively lock the slot).
    // Without the second fetch the home page would advertise slots
    // that are already booked.
    const [events, takenSlots] = await Promise.all([
      fetchAgenda(),
      fetchTakenSlots(),
    ]);
    const merged = [...events, ...takenSlots];
    mountPublicAgenda('agendaMountA22', 'A22', merged);
    mountPublicAgenda('agendaMountM15', 'M15', merged);
  } catch {
    // Fetch failed — fall back to the default-open schedule so the
    // section still looks alive (matches the member agenda's own
    // fallback). No toast — the section is read-only marketing.
    mountPublicAgenda('agendaMountA22', 'A22', []);
    mountPublicAgenda('agendaMountM15', 'M15', []);
  }
}

// =============================================================
// Mobile nav toggle (lives outside the re-rendered menu)
// =============================================================
function wireStaticNav(): void {
  const toggle = $('#navToggle') as HTMLElement | null;
  const menu = $('#navMenu') as HTMLElement | null;
  if (!toggle || !menu) return;
  toggle.addEventListener('click', () => {
    const open = menu.classList.toggle('is-open');
    toggle.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? t('nav.menuClose') : t('nav.menuOpen'));
  });
  menu.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.tagName === 'A' && menu.classList.contains('is-open')) {
      menu.classList.remove('is-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });
  // Close mobile menu when the language toggle is tapped
  menu.querySelector('.nav__lang')?.addEventListener('click', () => {
    if (menu.classList.contains('is-open')) {
      menu.classList.remove('is-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });
  // Same for the theme toggle.
  menu.querySelector('.nav__theme')?.addEventListener('click', () => {
    if (menu.classList.contains('is-open')) {
      menu.classList.remove('is-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });

  // ----- Mode toggle: click the `.nav__brand-num` pill to flip between
  // strength and yoga. The brand element lives in index.html inside the
  // brand <a>, so we attach to the span and stop propagation so the
  // wrapping link doesn't navigate away mid-flip. Keyboard: Enter/Space.
  const brandNum = document.querySelector<HTMLElement>('.nav__brand-num');
  if (brandNum) {
    brandNum.setAttribute('role', 'button');
    brandNum.setAttribute('tabindex', '0');
    brandNum.setAttribute(
      'aria-label',
      t('nav.toggleMode') || 'Toggle mode (Strength / Yoga)',
    );
    const trigger = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      // Play the 3D flip, then toggle the mode mid-flip so the swap
      // happens at the rotation apex — feels like the pill spins out
      // of one identity and into the other.
      if (brandNum.classList.contains('is-flipping')) return;
      brandNum.classList.add('is-flipping');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => {
        toggleMode();
        if (reduced) brandNum.classList.remove('is-flipping');
      }, reduced ? 0 : 200);   // mid-flip apex (~half of 480ms)
      const cleanup = () => brandNum.classList.remove('is-flipping');
      brandNum.addEventListener('animationend', cleanup, { once: true });
    };
    brandNum.addEventListener('click', trigger);
    brandNum.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') trigger(e);
    });
  }

  // Highlight on scroll
  window.addEventListener('scroll', () => {
    const nav = $('#nav');
    nav?.classList.toggle('is-scrolled', window.scrollY > 8);
  }, { passive: true });
}

// =============================================================
// Boot
// =============================================================
async function boot(): Promise<void> {
  wireStaticNav();
  applyI18n(document);

  // First-visit mode splash. Mounts before the heavy fetch so the
  // overlay is up by the time the JSON resolves. The promise resolves
  // with the chosen mode (or the user's existing persisted choice if
  // shouldShowSplash() returned false).
  //
  // Preload both splash halves in parallel with the JSON fetch so the
  // overlay paints already-decoded JPEGs (no top-to-bottom raster
  // decode when the user first sees the splash). preloadSplashImages()
  // is fire-and-forget for the cold cache miss path — the splash itself
  // awaits the decode, so even if the prelaod hasn't finished by the
  // time mountSplash runs, the user still gets a clean paint.
  void preloadSplashImages();
  const splashPromise: Promise<unknown> = shouldShowSplash() ? mountSplash() : Promise.resolve();

  try {
    tree = await fetchContent();
    // Apply the tenant theme to <html> as soon as we know it. Default
    // to 'sloth-forest' so a missing field still gets the brand look.
    //
    // URL override: `?theme=<id>` lets the admin Theme tab's preview
    // iframe render the public site with a non-persisted theme without
    // touching tenant config. Only valid TENANT_THEMES ids are accepted
    // so a typo or hostile URL can't flip the public site to garbage.
    const themeIds = new Set(TENANT_THEMES.map((th: any) => th.id));
    const urlTheme = (() => {
      try {
        const v = new URLSearchParams(window.location.search).get('theme');
        return v && themeIds.has(v) ? v : null;
      } catch {
        return null;
      }
    })();
    const theme = urlTheme || (tree as any)?.tenant?.theme || 'sloth-forest';
    document.documentElement.setAttribute('data-theme', theme);
    if (urlTheme) {
      (window as any).__SLOTH_THEME_PREVIEW__ = urlTheme;
    }
    // Expose tenant business data for widgets that hydrate from it
    // (opening-hours reads from window.__SLOTH_BUSINESS__).
    if ((tree as any)?.tenant) {
      (window as any).__SLOTH_TENANT__ = (tree as any).tenant;
      (window as any).__SLOTH_BUSINESS__ = (tree as any).business || null;
      (window as any).__SLOTH_SPLASH__ = (tree as any).splash || null;
    }
  } catch {
    tree = {
      pages: DEFAULT_PAGES,
      navItems: DEFAULT_PAGES.map((p, i) => ({
        id: `default-${p.id}`,
        kind: 'page' as const,
        pageId: p.id,
        label: { hu: '', en: '' },
        url: '',
        showInNav: p.showInNav,
        navOrder: p.navOrder ?? i * 10,
      })),
      seo: {},
      agenda: [],
      meta: { online: false, fetchedAt: new Date().toISOString() },
    };
  }
  stateTreeRef = tree;

  // Global splash dismiss handler — used by the new splash renderer.
  (window as any).__sloth_splash_dismiss = () => {
    const el = document.getElementById('sloth-splash');
    if (el) el.style.display = 'none';
    try {
      const tid = (tree as any)?.tenant?.id || 'unknown';
      localStorage.setItem(`sloth-splash-dismissed-${tid}`, '1');
    } catch {}
  };
  (window as any).__sloth_splash_pick = (key: string) => {
    // Legacy studio-picker behaviour: dispatch a custom event that the
    // existing mode/mode-picker listeners already handle.
    try {
      localStorage.setItem('studio333:mode', key === 'yoga' ? 'yoga' : 'strength');
    } catch {}
    document.documentElement.setAttribute('data-mode', key === 'yoga' ? 'yoga' : 'strength');
    (window as any).__sloth_splash_dismiss();
    location.reload();
  };

  // Wait for the splash to be dismissed before painting content so the
  // user never sees the wrong logo / accent under the overlay.
  await splashPromise;
  // If the user picked yoga in the splash, t() now resolves from
  // yoga.{locale}.json — re-apply i18n to pull the right copy before
  // the first paint of the hero.
  applyI18n(document);
  // Keep navItems in sync after admin edits.
  window.addEventListener('storage', () => { stateTreeRef = tree; renderNav(); });

  // Best-effort fetch of the current user (sets the cached user for
  // the header menu). Doesn't block boot.
  void fetchMe().catch(() => { /* not logged in */ });

  renderNav();
  renderHeaderUser();
  applyI18n($('#navMenu') ?? document);

  // History router
  window.addEventListener('popstate', () => {
    renderRoute();
    void refreshCachedUserAndMode();
  });

  // Intercept in-page anchor clicks so they route via the History API
  document.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement | null)?.closest('a');
    if (!t) return;
    const href = t.getAttribute('href') ?? '';
    if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    if (t.target === '_blank') return;
    if (href.startsWith('#')) return; // in-page anchor (section scroll)
    if (t.hasAttribute('data-native')) return; // opt-out for admin
    if (t.closest('[data-native]')) return;
    e.preventDefault();
    navigate(href);
  });

  // Default the URL to / if nothing else is set
  if (location.pathname === '' || location.pathname === '/index.html') {
    history.replaceState({}, '', '/');
  }
  renderRoute();

  // Re-render nav + content on locale change
  onLocaleChange(() => {
    renderNav();
    renderHeaderUser();
    applyI18n(document.body);
    applySeoForCurrentRoute();
  });

  // Re-render nav + content on mode change (strength ↔ yoga). The
  // brand flip animation handles the visual swap of the accent; this
  // re-render refreshes copy (hero/about/services/contact), the logo
  // (<picture> + CSS source-visibility handles the hero logo, but the
  // footer / brand suffix / dynamic sections need an i18n refresh),
  // and SEO tags.
  onModeChange(() => {
    // When a member is logged in, their discipline is authoritative —
    // if the brand-flip toggle moved the mode to a different flavor,
    // snap it back. Anonymous visitors keep the toggle's choice.
    if (isModeAuthoritative()) {
      const d = getCachedDiscipline();
      if (d && d !== getMode()) setMode(d);
    }
    renderNav();
    // renderNav() wipes the nav menu (replaceChildren); that includes the
    // login/profile block that renderHeaderUser() had previously appended.
    // Re-render it so the "Sign in" CTA survives a strength ↔ yoga toggle.
    renderHeaderUser();
    applyI18n(document.body);
    applySeoForCurrentRoute();
  });

  // Expose state for the admin SPA via a custom event
  window.dispatchEvent(new CustomEvent('content:ready', { detail: { tree } }));
}

boot();
void initialLocale; // keep TS quiet about unused; used at init time